import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  apiErrorText, cleanExperienceInput, EXPERIENCE_HINT, EXPERIENCE_MAX_LENGTH, parseExperience,
} from '../utils/experience';
import { campaignAPI } from '../services/api';
import SkillsInput from '../components/common/SkillsInput';

/**
 * Manual "Create Position" — mirrors ReachOut's manual position flow:
 * basic details → attach JD (write or upload) → skills → create.
 * Questions/interviews are set up afterwards from the position page. Skills left empty are
 * filled from the JD in the background, the same as positions synced from ReachOut.
 */

function generateJobCode() {
  return 'wz-' + Math.random().toString(36).slice(2, 6).toUpperCase();
}

const inputStyle = {
  width: '100%', border: '1px solid #E5E7EB', borderRadius: 8, padding: '9px 12px', fontSize: 13,
  color: '#111827', outline: 'none', background: 'white', transition: 'border-color .15s', fontFamily: 'inherit',
};
const labelStyle = { fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 6 };
const sectionTitleStyle = { fontSize: 13, fontWeight: 700, color: '#111827', margin: '0 0 14px' };
const focus = (e) => { e.target.style.borderColor = '#6366F1'; };
const blur = (e) => { e.target.style.borderColor = '#E5E7EB'; };

function Req() {
  return <span style={{ color: '#EF4444' }}>*</span>;
}

function JdTab({ active, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        padding: '6px 12px', fontSize: 12, fontWeight: 600, borderRadius: 7, cursor: 'pointer', fontFamily: 'inherit',
        border: `1px solid ${active ? '#6366F1' : '#E5E7EB'}`,
        background: active ? '#EEF2FF' : 'white',
        color: active ? '#4338CA' : '#6B7280',
      }}
    >
      {children}
    </button>
  );
}

const NewPosition = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const [form, setForm] = useState({
    name: '',
    client_name: '',
    job_code: generateJobCode(),
    job_role: '',
    experience_years: '',
    language: 'en',
    job_description: '',
    required_skills: [],
  });
  const [roleTouched, setRoleTouched] = useState(false);
  const [jdTab, setJdTab] = useState('manual'); // manual | upload
  const [jdFileName, setJdFileName] = useState('');
  const [extracting, setExtracting] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const handleFile = async (file) => {
    if (!file) return;
    const ext = (file.name.split('.').pop() || '').toLowerCase();
    if (!['pdf', 'docx', 'txt'].includes(ext)) {
      setError('Unsupported file type. Upload a PDF, DOCX or TXT file.');
      return;
    }
    setExtracting(true);
    setError('');
    try {
      const res = await campaignAPI.extractJd(file);
      set({ job_description: res.data?.text || '' });
      setJdFileName(file.name);
      setJdTab('manual');
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not read the JD file.');
    } finally {
      setExtracting(false);
    }
  };

  const missing = [];
  if (!form.name.trim()) missing.push('Position Name');
  if (form.job_role.trim().length < 3) missing.push('Job Role (min 3 characters)');
  const experience = parseExperience(form.experience_years);
  if (!String(form.experience_years).trim()) missing.push('Experience (years)');
  if (!form.job_description.trim()) missing.push('Job Description');

  const handleCreate = async () => {
    if (missing.length) {
      setError(`Please fill in: ${missing.join(', ')}.`);
      return;
    }
    if (experience.error) {
      setError(`Experience: ${experience.error}`);
      return;
    }
    setSaving(true);
    setError('');
    try {
      const res = await campaignAPI.create({
        name: form.name.trim(),
        job_role: form.job_role.trim(),
        client_name: form.client_name.trim() || null,
        job_code: form.job_code.trim() || null,
        job_description: form.job_description.trim(),
        experience_years: experience.label || null,
        required_skills: form.required_skills,
        language: form.language,
      });
      navigate(`/admin/campaigns/${res.data.id}`);
    } catch (err) {
      setError(apiErrorText(err, 'Failed to create position'));
      setSaving(false);
    }
  };

  return (
    <div className="create-position-page" style={{ flex: 1, overflowY: 'auto', minHeight: 0, padding: '28px 24px', background: '#F9FAFB' }}>
      <div style={{ maxWidth: 640, margin: '0 auto' }}>
        <button
          type="button"
          onClick={() => navigate('/admin/campaigns')}
          style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#6B7280', background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'inherit', padding: '5px 0', marginBottom: 20 }}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14"><polyline points="15 18 9 12 15 6" /></svg>
          Back to Positions
        </button>

        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <h1 style={{ fontSize: 20, fontWeight: 700, color: '#111827', letterSpacing: '-.3px', marginBottom: 4 }}>Create New Position</h1>
          <p style={{ fontSize: 13, color: '#6B7280' }}>
            Add the position details, attach the job description and skills.
          </p>
        </div>

        {error && (
          <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: 13, color: '#991B1B' }}>
            {error}
          </div>
        )}

        <div style={{ background: '#FFFFFF', border: '1px solid #E5E7EB', borderRadius: 14, padding: '26px 28px', boxShadow: '0 1px 3px rgba(15,23,42,.06)' }}>
          {/* ── Position details ── */}
          <h3 style={sectionTitleStyle}>Position Details</h3>

          <div style={{ marginBottom: 16 }}>
            <label style={labelStyle}>Position Name <Req /></label>
            <input
              value={form.name}
              onChange={(e) => {
                const name = e.target.value;
                set(roleTouched ? { name } : { name, job_role: name });
              }}
              placeholder="e.g. Senior React Developer"
              style={inputStyle} onFocus={focus} onBlur={blur}
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 16 }}>
            <div>
              <label style={labelStyle}>Client / Company</label>
              <input
                value={form.client_name}
                onChange={(e) => set({ client_name: e.target.value })}
                placeholder="e.g. Acme Corp"
                style={inputStyle} onFocus={focus} onBlur={blur}
              />
            </div>
            <div>
              <label style={labelStyle}>
                Job Code
                <span style={{ fontSize: 10, fontWeight: 400, color: '#9CA3AF', marginLeft: 6 }}>auto-generated</span>
              </label>
              <div style={{ display: 'flex', gap: 6 }}>
                <input
                  value={form.job_code}
                  onChange={(e) => set({ job_code: e.target.value })}
                  placeholder="wz-XXXX"
                  style={{ ...inputStyle, flex: 1, width: 'auto' }} onFocus={focus} onBlur={blur}
                />
                <button
                  type="button" title="Regenerate code"
                  onClick={() => set({ job_code: generateJobCode() })}
                  style={{ padding: '0 10px', borderRadius: 8, border: '1px solid #E5E7EB', background: '#F9FAFB', cursor: 'pointer', fontSize: 14 }}
                >
                  ↻
                </button>
              </div>
            </div>
          </div>

          <div style={{ marginBottom: 16 }}>
            <label style={labelStyle}>Job Role <Req /></label>
            <input
              value={form.job_role}
              onChange={(e) => { setRoleTouched(true); set({ job_role: e.target.value }); }}
              placeholder="e.g. Senior Software Engineer"
              style={inputStyle} onFocus={focus} onBlur={blur}
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 24 }}>
            <div>
              <label style={labelStyle}>Experience (years) <Req /></label>
              <input
                value={form.experience_years}
                onChange={(e) => set({ experience_years: cleanExperienceInput(e.target.value) })}
                maxLength={EXPERIENCE_MAX_LENGTH}
                placeholder="e.g. 3, 2-3 or 10+"
                aria-invalid={!!experience.error}
                aria-describedby="experience-help"
                style={{ ...inputStyle, ...(experience.error ? { borderColor: '#DC2626' } : {}) }}
                onFocus={focus}
                onBlur={(e) => {
                  blur(e);
                  if (experience.error) e.target.style.borderColor = '#DC2626';
                  if (experience.label) set({ experience_years: experience.label });
                }}
              />
              <div id="experience-help" style={{ fontSize: 11, marginTop: 4, color: experience.error ? '#DC2626' : '#6B7280' }}>
                {experience.error || EXPERIENCE_HINT}
              </div>
            </div>
            <div>
              <label style={labelStyle}>Interview Language</label>
              <select
                value={form.language}
                onChange={(e) => set({ language: e.target.value })}
                style={{ ...inputStyle, cursor: 'pointer' }} onFocus={focus} onBlur={blur}
              >
                <option value="en">English</option>
                <option value="es">Spanish (Bilingual)</option>
              </select>
            </div>
          </div>

          {/* ── Job description ── */}
          <div style={{ borderTop: '1px solid #F1F5F9', paddingTop: 20, marginBottom: 24 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
              <h3 style={{ ...sectionTitleStyle, margin: 0 }}>Job Description <Req /></h3>
              <div style={{ display: 'flex', gap: 6 }}>
                <JdTab active={jdTab === 'manual'} onClick={() => setJdTab('manual')}>✎ Write manually</JdTab>
                <JdTab active={jdTab === 'upload'} onClick={() => setJdTab('upload')}>↑ Upload JD</JdTab>
              </div>
            </div>

            {jdTab === 'manual' ? (
              <>
                {jdFileName && (
                  <div style={{ fontSize: 11, color: '#4338CA', marginBottom: 6 }}>
                    Extracted from <strong>{jdFileName}</strong> — review and edit below.
                  </div>
                )}
                <textarea
                  value={form.job_description}
                  onChange={(e) => set({ job_description: e.target.value })}
                  placeholder={'Write or paste the job description here.\n\nTip: use headings like "Responsibilities", "Requirements", "Nice to have".'}
                  rows={12}
                  style={{ ...inputStyle, resize: 'vertical', lineHeight: 1.5, minHeight: 220 }}
                  onFocus={focus} onBlur={blur}
                />
              </>
            ) : (
              <>
                <div
                  role="button"
                  tabIndex={0}
                  onClick={() => !extracting && fileInputRef.current?.click()}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') fileInputRef.current?.click(); }}
                  onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
                  onDragLeave={() => setDragActive(false)}
                  onDrop={(e) => { e.preventDefault(); setDragActive(false); handleFile(e.dataTransfer.files?.[0]); }}
                  style={{
                    border: `1.5px dashed ${dragActive ? '#6366F1' : '#D1D5DB'}`, borderRadius: 10,
                    background: dragActive ? '#EEF2FF' : '#F9FAFB', padding: '32px 16px', textAlign: 'center',
                    cursor: extracting ? 'wait' : 'pointer',
                  }}
                >
                  <div style={{ fontSize: 26, color: '#6B7280', marginBottom: 8 }}>↑</div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#111827' }}>
                    {extracting ? 'Extracting text…' : 'Drop a file here or click to browse'}
                  </div>
                  <div style={{ fontSize: 11, color: '#6B7280', marginTop: 4 }}>PDF, DOCX, TXT supported</div>
                </div>
                <input
                  ref={fileInputRef} type="file" accept=".pdf,.docx,.txt" style={{ display: 'none' }}
                  onChange={(e) => { handleFile(e.target.files?.[0]); e.target.value = ''; }}
                />
                {form.job_description.trim() && (
                  <div style={{ fontSize: 11, color: '#6B7280', marginTop: 8 }}>
                    Uploading a file replaces the current job description.
                  </div>
                )}
              </>
            )}
          </div>

          {/* ── Skills ── */}
          <div style={{ borderTop: '1px solid #F1F5F9', paddingTop: 20 }}>
            <h3 style={{ ...sectionTitleStyle, marginBottom: 4 }}>Required Skills</h3>
            <p style={{ fontSize: 11, color: '#6B7280', margin: '0 0 10px' }}>
              Leave empty to have skills picked from the job description automatically.
            </p>
            <SkillsInput
              value={form.required_skills}
              onChange={(required_skills) => set({ required_skills })}
              placeholder="Type a skill and press Enter to add"
            />
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 18 }}>
          <button
            type="button"
            onClick={() => navigate('/admin/campaigns')}
            style={{ padding: '9px 18px', borderRadius: 8, border: '1px solid #E5E7EB', background: 'white', color: '#374151', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleCreate}
            disabled={saving || extracting}
            style={{
              padding: '9px 20px', borderRadius: 8, border: 'none', fontSize: 13, fontWeight: 600, fontFamily: 'inherit',
              background: '#6366F1', color: 'white', cursor: saving ? 'wait' : 'pointer', opacity: (saving || extracting || missing.length) ? 0.6 : 1,
            }}
          >
            {saving ? 'Creating…' : 'Create Position'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default NewPosition;
